import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../button";
import { formatTextMaxChars, getProductSellingPrice } from "@utilities/functions";
import { ModalSelectVariation } from "../modals";
import { AddProductInBasketType, Basket, BasketItem, Product } from "@utilities/types";

type SelectedVariation = {
  selectedColor: string | null;
  selectedSize: string | null;
}

interface ProductCardProps {
  product: Product;
  basket: Basket | null;
  onAddProduct: (basketElement: Partial<AddProductInBasketType>) => void;
  onRemoveProduct: (basketElement: AddProductInBasketType) => void
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  basket,
  onAddProduct,
  onRemoveProduct,
}) => {

  const [isModalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const productHasVariations = useMemo(() => (
    product.variationType === 'color' ||
    product.variationType === 'size' ||
    product.variationType === 'both'
  ), [product]);

  const price = useMemo(() => getProductSellingPrice(product), [product]);
  const totalQuantity = useMemo(() => {
    if (product.variationType === 'none') {
      //@ts-ignore
      return product.variations.quantity;
    } else if (product.variationType === 'color') {
      //@ts-ignore
      return product.variations.colors.reduce((acc, color) => acc + (color.quantity || 0), 0);
    } else if (product.variationType === 'size') {
      //@ts-ignore
      return product.variations.sizes.reduce((acc, size) => acc + (size.quantity || 0), 0);
    } else if (product.variationType === 'both') {
      //@ts-ignore
      return product.variations.colorSizeData.reduce((acc, colorSize) => acc + colorSize.sizes.reduce((sum, size) => sum + (size.quantity || 0), 0), 0);
    }
    return 0;
  }, [product]);

  const discountedPrice = useMemo(() => {
    if (product._promotionID) {
      const discount = product._promotionID.type === 'percentage'
        ? price * product._promotionID.value / 100
        : product._promotionID.value;
      return price - discount;
    }
    return 0;
  }, [price, product]);

  const currentImageUrl = useMemo(() => {
    if (!product.images || product.images.length === 0) return null;
    const currentImage = product.images[currentImageIndex];
    return `${process.env.REACT_APP_IMAGE_BASE_URL}/${currentImage?.fileDir}/${currentImage?.filename}`;
  }, [product.images, currentImageIndex]);

  const quantity = useMemo(() => {
    if (!basket || !basket.basket) return 0;
    const itemIndex = basket.basket.findIndex(item => item.product._id === product._id);
    return itemIndex !== -1 ? basket.basket[itemIndex].quantity : 0;
  }, [basket, product]);

  const basketItem: BasketItem = useMemo(() => ({
    product: product,
    quantity,
    price,
    discountedPrice,
    // promotion,
  }), [discountedPrice, price, product, quantity]);

  useEffect(() => {
    if (product.images && product.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [product]);

  const handleAddProduct = useCallback((selectedVariations?: SelectedVariation) => {
    if (selectedVariations?.selectedColor) {
      basketItem.selectedColor = selectedVariations?.selectedColor
    };
    if (selectedVariations?.selectedSize) {
      basketItem.selectedSize = selectedVariations?.selectedSize
    };

    onAddProduct({
      basketID: basket?._id || undefined,
      productItem: basketItem,
    });
  }, [basket, basketItem, onAddProduct]);

  const handleRemoveProduct = useCallback(() => {
    if (!basket) return;

    onRemoveProduct({
      basketID: basket._id,
      productItem: basketItem,
    });
  }, [basket, basketItem, onRemoveProduct]);

  const renderPaginationDots = useCallback(() => {
    if (!product.images || product.images.length <= 1) return null;

    return (
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
        {
          product.images.map((_, index) => (
            <span
              key={index}
              className={`inline-block h-1.5 w-1.5 rounded-full 
              ${currentImageIndex === index
                  ? 'bg-white' : 'bg-white/50'
                }`}
            />
          ))
        }
      </div>
    );
  }, [product, currentImageIndex]);

  return (
    <>
      <div
        onClick={() => quantity <= 0 && productHasVariations ? setModalOpen(true) : handleAddProduct()}
        className="card card-compact w-full bg-white shadow-md rounded-lg overflow-hidden relative"
      >
        <figure className="relative">
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt={product.name}
              className="w-full h-24 sm:h-28 md:h-40 lg:h-48 object-contain cursor-pointer transition-opacity duration-300"
            />
          ) : (
            <div className="w-full h-24 sm:h-28 md:h-40 lg:h-48 bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500 text-sm">Image non disponible</span>
            </div>
          )}

          {renderPaginationDots()}

          {quantity > 0 && (
            <div className="absolute inset-0 bg-pink-400/50 flex items-center justify-center gap-3">
              <Button
                className="btn btn-circle btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveProduct();
                }}
              >-</Button>
              <span className="text-white font-bold text-md">{quantity}</span>
              <Button
                className="btn btn-circle btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  productHasVariations ? setModalOpen(true) :
                    handleAddProduct();
                }}
              >+</Button>
            </div>
          )}
        </figure>
        <div className="p-2">
          <h2 className="card-title text-[0.75rem] sm:text-xs lg:text-sm text-gray-700 font-medium leading-tight">
            {formatTextMaxChars(product.name, 20)}
          </h2>
          <div className="flex justify-between items-center">
            <label className="text-xs text-gray-700 font-semibold leading-tight">
              ({totalQuantity} qte)
            </label>
            {discountedPrice > 0 ? (
              <div className="flex flex-col items-end">
                <span className="text-[0.65rem] sm:text-[0.7rem] text-gray-400 line-through">
                  {price.toLocaleString()} Fcfa
                </span>
                <span className="text-[0.75rem] sm:text-xs lg:text-sm text-red-500 font-bold leading-tight">
                  {discountedPrice.toLocaleString()} Fcfa
                </span>
              </div>
            ) : (
              <p className="text-[0.75rem] sm:text-xs lg:text-sm text-gray-700 font-semibold leading-tight">
                {price.toLocaleString()} Fcfa
              </p>
            )}
          </div>
          {/* Badge promo */}
          {discountedPrice > 0 && product._promotionID && (
            <div className="mt-1">
              <span className="inline-block bg-red-500 text-white text-[0.6rem] px-1.5 py-0.5 rounded-full">
                -{product._promotionID.type === 'percentage'
                  ? `${product._promotionID.value}%`
                  : `${product._promotionID.value} Fcfa`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour sélectionner la variation */}
      {isModalOpen && (
        <ModalSelectVariation
          isOpen={isModalOpen}
          product={product}
          onClose={() => setModalOpen(false)}
          onValidate={(selectedColor, selectedSize) => {
            handleAddProduct({ selectedColor, selectedSize });
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
};
